import {
  type MRT_ColumnDef,
  type MRT_TableOptions,
  type MRT_RowData,
  type MRT_Row,
  type MRT_TableInstance,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useEffect, useReducer } from "react";
import type React from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import {
  type APIPaginatedTableState,
  tableInitialState,
  tableReducer,
} from "./states";
import { DataError } from "../error/data-error";
import { DataEmpty } from "../error/data-empty";
import type { PaginatedResponse } from "@/types/common/api-types";

// These types are purely from Cursor and before they were any, so high chance they are wrong
interface APIPaginatedTableProps<
  TData extends MRT_RowData,
  TQueryParams = unknown,
> {
  columns: MRT_ColumnDef<TData>[];
  mrtTableOptions?: Omit<MRT_TableOptions<TData>, "columns" | "data">;
  onRenderRowActionMenuItems?: (props: {
    closeMenu: () => void;
    row: MRT_Row<TData>;
    staticRowIndex?: number;
    table: MRT_TableInstance<TData>;
    reactQuery: UseQueryResult<PaginatedResponse<TData>, Error>;
  }) => React.ReactNode[] | undefined;
  onRenderToolbarInternalActions?: (props: {
    table: MRT_TableInstance<TData>;
    reactQuery: UseQueryResult<PaginatedResponse<TData>, Error>;
  }) => React.ReactNode;
  reactQuery: (
    params: TQueryParams
  ) => UseQueryResult<PaginatedResponse<TData>, Error>;
  reactQueryFilters: (state: APIPaginatedTableState) => TQueryParams;
  reactQueryOnChange?: (
    qData: UseQueryResult<PaginatedResponse<TData>, Error>
  ) => void;
  titleTopic: string;
  contentTopic: string;
}

export function APIPaginatedTable<
  TData extends MRT_RowData,
  TQueryParams = unknown,
>({
  columns,
  mrtTableOptions,
  onRenderRowActionMenuItems,
  onRenderToolbarInternalActions,
  reactQuery,
  reactQueryFilters: mapReactQueryFilters,
  reactQueryOnChange: onReactQueryChange,
  titleTopic,
  contentTopic,
}: APIPaginatedTableProps<TData, TQueryParams>) {
  const [state, dispatch] = useReducer(tableReducer, tableInitialState);

  const qData = reactQuery(mapReactQueryFilters(state));

  const hasActiveFilters = state.columnFilters.length > 0;
  const hasNoData =
    qData.data && qData.data.items.length === 0 && !qData.isPending;
  const shouldShowDataEmpty = hasNoData && !hasActiveFilters;

  const table = useMaterialReactTable({
    columns,
    data: qData.data ? qData.data.items : [],
    displayColumnDefOptions: { "mrt-row-actions": { header: "", size: 1 } },
    enableColumnActions: false,
    enableHiding: false,
    enableFilterMatchHighlighting: false,
    enablePagination: !qData.isPending,
    enableRowActions: true,
    enableSorting: false,
    initialState: { pagination: state.pagination, density: "compact" },
    manualFiltering: true,
    manualPagination: true,
    manualSorting: false,

    onColumnFiltersChange(updater) {
      const v =
        typeof updater === "function" ? updater(state.columnFilters) : updater;
      dispatch({ type: "SET_COLUMN_FILTERS", payload: v });
    },
    onPaginationChange(updater) {
      const v =
        typeof updater === "function" ? updater(state.pagination) : updater;
      dispatch({ type: "SET_PAGINATION", payload: v });
    },
    pageCount: qData.data ? qData.data.pages : undefined,
    positionActionsColumn: "last",
    renderRowActionMenuItems(props) {
      if (onRenderRowActionMenuItems) {
        return onRenderRowActionMenuItems({ reactQuery: qData, ...props });
      }
    },
    renderToolbarInternalActions(props) {
      if (onRenderToolbarInternalActions) {
        return onRenderToolbarInternalActions({ reactQuery: qData, ...props });
      }
    },
    renderEmptyRowsFallback:
      hasActiveFilters && hasNoData
        ? () => (
            <div className="flex items-center justify-center h-32 text-muted-foreground">
              No results found. Try adjusting your filters.
            </div>
          )
        : undefined,
    rowCount: qData.data ? qData.data.total : undefined,
    state: {
      columnFilters: state.columnFilters,
      pagination: state.pagination,
      showLoadingOverlay: qData.isPlaceholderData || qData.isRefetching,
      showSkeletons: qData.isPending,
    },
    ...mrtTableOptions,
  });

  useEffect(() => {
    if (onReactQueryChange) {
      onReactQueryChange(qData);
    }
  }, [qData]);

  if (qData.isError) {
    return <DataError topic={titleTopic} />;
  }

  if (shouldShowDataEmpty) {
    return <DataEmpty titleTopic={titleTopic} contentTopic={contentTopic} />;
  }

  return <MaterialReactTable table={table} />;
}
